/**
 * Scan Audit Client Library
 *
 * Tracks all barcode scan attempts for security and analytics
 * Stores in IndexedDB for offline support, syncs to server when online
 * Poka-Yoke: Complete audit trail prevents fraud and tracks issues
 */

export interface ScanAuditEntry {
  id?: number; // Auto-generated by IndexedDB
  barcode: string;
  timestamp: number;
  success: boolean;
  errorMessage?: string;
  deviceInfo: string;
  userAgent: string;
  screenResolution: string;
  connectionType: string;
  synced: boolean;
}

const DB_NAME = "radiomisr_audit";
const DB_VERSION = 1;
const STORE_NAME = "scan_logs";
const MAX_ENTRIES = 1000; // Keep last 1000 entries

/**
 * Initialize IndexedDB for audit logging
 */
function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined" || !window.indexedDB) {
      reject(new Error("IndexedDB not available"));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => {
      reject(new Error("Failed to open audit database"));
    };

    request.onsuccess = () => {
      resolve(request.result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object store if it doesn't exist
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, {
          keyPath: "id",
          autoIncrement: true,
        });

        // Create indexes for efficient querying
        store.createIndex("timestamp", "timestamp", { unique: false });
        store.createIndex("barcode", "barcode", { unique: false });
        store.createIndex("synced", "synced", { unique: false });
        store.createIndex("success", "success", { unique: false });
      }
    };
  });
}

/**
 * Get device information for audit trail
 */
function getDeviceInfo(): {
  deviceInfo: string;
  userAgent: string;
  screenResolution: string;
  connectionType: string;
} {
  if (typeof window === "undefined") {
    return {
      deviceInfo: "server",
      userAgent: "server",
      screenResolution: "0x0",
      connectionType: "unknown",
    };
  }

  // Device type detection
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
  const isTablet = /iPad|Android/i.test(navigator.userAgent) && window.innerWidth >= 768;

  let deviceType = "desktop";
  if (isTablet) deviceType = "tablet";
  else if (isMobile) deviceType = "mobile";

  // Connection type (if available)
  const connection =
    (navigator as any).connection ||
    (navigator as any).mozConnection ||
    (navigator as any).webkitConnection;
  const connectionType = connection?.effectiveType || "unknown";

  return {
    deviceInfo: deviceType,
    userAgent: navigator.userAgent,
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    connectionType,
  };
}

/**
 * Log a scan attempt
 * Poka-Yoke: Always log, even on failure
 */
export async function logScanAttempt(
  barcode: string,
  success: boolean,
  errorMessage?: string
): Promise<void> {
  try {
    const db = await openDatabase();
    const deviceInfo = getDeviceInfo();

    const entry: ScanAuditEntry = {
      barcode,
      timestamp: Date.now(),
      success,
      errorMessage,
      ...deviceInfo,
      synced: false,
    };

    // Store in IndexedDB
    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.add(entry);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error("Failed to add audit entry"));
    });

    // Cleanup old entries (keep only last MAX_ENTRIES)
    await cleanupOldEntries(db);

    db.close();

    // Attempt to sync if online
    if (navigator.onLine) {
      syncPendingLogs().catch(() => {
        // Ignore sync errors - will retry later
      });
    }

    console.log("[Audit] Scan logged:", barcode, success ? "success" : "failed");
  } catch (error) {
    console.error("[Audit] Failed to log scan:", error);
    // Don't throw - logging failures shouldn't break the app
  }
}

/**
 * Get recent scan logs
 */
export async function getRecentScans(limit: number = 10): Promise<ScanAuditEntry[]> {
  try {
    const db = await openDatabase();

    const entries = await new Promise<ScanAuditEntry[]>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index("timestamp");
      const request = index.openCursor(null, "prev"); // Descending order

      const results: ScanAuditEntry[] = [];
      let count = 0;

      request.onsuccess = (event) => {
        const cursor = (event.target as IDBRequest).result;
        if (cursor && count < limit) {
          results.push(cursor.value);
          count++;
          cursor.continue();
        } else {
          resolve(results);
        }
      };

      request.onerror = () => reject(new Error("Failed to fetch scan logs"));
    });

    db.close();
    return entries;
  } catch (error) {
    console.error("[Audit] Failed to get recent scans:", error);
    return [];
  }
}

/**
 * Get scan statistics
 */
export async function getScanStats(): Promise<{
  total: number;
  successful: number;
  failed: number;
  lastScanTime: number | null;
}> {
  try {
    const db = await openDatabase();

    const stats = await new Promise<{
      total: number;
      successful: number;
      failed: number;
      lastScanTime: number | null;
    }>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => {
        const entries: ScanAuditEntry[] = request.result;
        const total = entries.length;
        const successful = entries.filter((e) => e.success).length;
        const failed = total - successful;
        const lastScanTime =
          entries.length > 0 ? Math.max(...entries.map((e) => e.timestamp)) : null;

        resolve({ total, successful, failed, lastScanTime });
      };

      request.onerror = () => reject(new Error("Failed to get stats"));
    });

    db.close();
    return stats;
  } catch (error) {
    console.error("[Audit] Failed to get stats:", error);
    return { total: 0, successful: 0, failed: 0, lastScanTime: null };
  }
}

/**
 * Cleanup old entries (keep only last MAX_ENTRIES)
 */
async function cleanupOldEntries(db: IDBDatabase): Promise<void> {
  try {
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);
    const countRequest = store.count();

    countRequest.onsuccess = () => {
      const count = countRequest.result;

      if (count > MAX_ENTRIES) {
        // Delete oldest entries
        const deleteCount = count - MAX_ENTRIES;
        const index = store.index("timestamp");
        const cursorRequest = index.openCursor();
        let deleted = 0;

        cursorRequest.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor && deleted < deleteCount) {
            cursor.delete();
            deleted++;
            cursor.continue();
          }
        };
      }
    };
  } catch (error) {
    console.warn("[Audit] Cleanup failed:", error);
  }
}

/**
 * Sync pending logs to server
 * This is called automatically when online, but can be manually triggered
 */
export async function syncPendingLogs(): Promise<void> {
  if (!navigator.onLine) {
    console.log("[Audit] Offline - skipping sync");
    return;
  }

  try {
    const db = await openDatabase();

    // Get unsynced entries
    const unsyncedEntries = await new Promise<ScanAuditEntry[]>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], "readonly");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll(); // Get all entries

      request.onsuccess = () => {
        // Filter for unsynced entries
        const allEntries: ScanAuditEntry[] = request.result;
        const unsynced = allEntries.filter((entry) => !entry.synced);
        resolve(unsynced);
      };
      request.onerror = () => reject(new Error("Failed to fetch unsynced logs"));
    });

    if (unsyncedEntries.length === 0) {
      db.close();
      return;
    }

    console.log(`[Audit] Syncing ${unsyncedEntries.length} logs to server...`);

    // Send to server (implement your API endpoint)
    // For now, we'll just mark as synced locally
    // TODO: Implement server-side audit log storage

    // Mark entries as synced
    const transaction = db.transaction([STORE_NAME], "readwrite");
    const store = transaction.objectStore(STORE_NAME);

    for (const entry of unsyncedEntries) {
      if (entry.id) {
        const updatedEntry = { ...entry, synced: true };
        store.put(updatedEntry);
      }
    }

    await new Promise<void>((resolve, reject) => {
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(new Error("Failed to update sync status"));
    });

    db.close();
    console.log(`[Audit] Synced ${unsyncedEntries.length} logs successfully`);
  } catch (error) {
    console.error("[Audit] Sync failed:", error);
    throw error;
  }
}

/**
 * Clear all audit logs (admin only - for testing/development)
 */
export async function clearAllLogs(): Promise<void> {
  try {
    const db = await openDatabase();

    await new Promise<void>((resolve, reject) => {
      const transaction = db.transaction([STORE_NAME], "readwrite");
      const store = transaction.objectStore(STORE_NAME);
      const request = store.clear();

      request.onsuccess = () => resolve();
      request.onerror = () => reject(new Error("Failed to clear logs"));
    });

    db.close();
    console.log("[Audit] All logs cleared");
  } catch (error) {
    console.error("[Audit] Failed to clear logs:", error);
    throw error;
  }
}

/**
 * Export logs to CSV (for admin/debugging)
 */
export async function exportLogsToCSV(): Promise<string> {
  try {
    const logs = await getRecentScans(MAX_ENTRIES);

    // CSV header
    let csv = "ID,Barcode,Timestamp,Success,Error,Device,UserAgent,Resolution,Connection,Synced\n";

    // CSV rows
    logs.forEach((log) => {
      const row = [
        log.id || "",
        log.barcode,
        new Date(log.timestamp).toISOString(),
        log.success ? "YES" : "NO",
        log.errorMessage || "",
        log.deviceInfo,
        `"${log.userAgent.replace(/"/g, '""')}"`, // Escape quotes
        log.screenResolution,
        log.connectionType,
        log.synced ? "YES" : "NO",
      ].join(",");
      csv += row + "\n";
    });

    return csv;
  } catch (error) {
    console.error("[Audit] Export failed:", error);
    throw error;
  }
}
